// Phase 283 — Public "Book Onboarding Call" page
// Lives at /book-call. Linked from the Pitch Email's primary CTA so prospects
// stay on out-play.in. Submissions create a `crm_leads` row + ping super-admins.
import { useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Loader2, Send, CalendarDays, ArrowLeft, Sparkles, MessageCircle, Shield,
} from 'lucide-react';
import { LOGO_URL } from '@/lib/brand';
import { getTelemetrySessionId } from '@/hooks/useWebTelemetry';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SLOT_PRESETS = [
  'Today afternoon (IST)',
  'Tomorrow morning (IST)',
  'Tomorrow evening (IST)',
  'This weekend',
  'Pick from email reply',
];

export default function BookCall() {
  const [params] = useSearchParams();
  const utmSource = params.get('utm_source') || '';

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    academy_name: '',
    preferred_slot: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please add your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const session_id = getTelemetrySessionId() || undefined;
      const res = await axios.post(`${BACKEND}/api/public/book-call`, {
        ...form,
        notes: utmSource ? `[${utmSource}] ${form.notes}`.trim() : form.notes,
        session_id,
      }, { timeout: 12000 });
      if (res.data?.success) {
        toast.success(res.data.message || 'Booked! We will be in touch.');
        setSubmitted(true);
      } else {
        toast.error('Could not book your call. Please try again.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Network error. Please retry.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-amber-50 dark:from-slate-950 dark:via-emerald-950/30 dark:to-slate-950">
      <header className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="book-call-home-link">
          <img src={LOGO_URL} alt="Out-Play" className="h-7 w-7" />
          <span className="font-display font-bold text-lg text-slate-900 dark:text-slate-100">
            Out-Play
          </span>
        </Link>
        <Link to="/" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to site
        </Link>
      </header>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 mt-6">
          {/* Left rail — value props */}
          <div className="lg:col-span-2 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-[10px] font-bold tracking-wider uppercase">
              <Sparkles className="w-3 h-3" /> Founder-Led Onboarding
            </div>
            <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 dark:text-slate-100 leading-tight">
              Book your 30-min onboarding call
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed">
              The founder walks you through Out-Play personally — show us your academy&apos;s
              current chaos and we&apos;ll show you exactly how to move every workflow on day 1.
            </p>
            <ul className="space-y-2.5 text-sm mt-4">
              <li className="flex items-start gap-2.5">
                <CalendarDays className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                <span><strong>Pick any slot.</strong> We work IST and book within 24h.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <MessageCircle className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                <span><strong>No script, no sales pitch.</strong> Walk us through your real ops.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Shield className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                <span><strong>30-day pilot free.</strong> Migrate students + run a batch end-to-end.</span>
              </li>
            </ul>
          </div>

          {/* Right rail — form */}
          <Card
            className="lg:col-span-3 border-emerald-200 dark:border-emerald-800 shadow-xl"
            data-testid="book-call-card"
          >
            <CardContent className="p-6 lg:p-8">
              {submitted ? (
                <div className="text-center py-10" data-testid="book-call-success">
                  <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center mb-4">
                    <CalendarDays className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-display font-bold mb-2">You&apos;re on the founder&apos;s list.</h2>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                    Watch your inbox — we&apos;ll email a calendar link within a few hours and pick
                    a slot that fits your schedule.
                  </p>
                  <Button asChild variant="outline" className="mt-6" data-testid="book-call-back-home">
                    <Link to="/">Back to homepage</Link>
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="book-call-form">
                  <h2 className="text-lg font-display font-semibold mb-1">Tell us about you</h2>
                  <p className="text-xs text-muted-foreground mb-3">All fields except notes are required.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Full name *</Label>
                      <Input
                        data-testid="book-call-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Suresh Kumar"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Work email *</Label>
                      <Input
                        data-testid="book-call-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@academy.in"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">WhatsApp / phone</Label>
                      <Input
                        data-testid="book-call-phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+91 98765..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Academy / organisation</Label>
                      <Input
                        data-testid="book-call-academy"
                        value={form.academy_name}
                        onChange={(e) => setForm({ ...form, academy_name: e.target.value })}
                        placeholder="Mumbai Cricket Co."
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Preferred slot</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {SLOT_PRESETS.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setForm({ ...form, preferred_slot: slot })}
                          data-testid={`book-call-slot-${slot.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`}
                          className={`text-[11px] px-2.5 py-1.5 rounded-full border transition-colors ${
                            form.preferred_slot === slot
                              ? 'bg-emerald-600 border-emerald-600 text-white'
                              : 'border-slate-200 dark:border-slate-700 text-muted-foreground hover:border-emerald-400 hover:text-emerald-700'
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">What should we focus on? (Optional)</Label>
                    <Textarea
                      data-testid="book-call-notes"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="50 students currently on Excel · Mainly worried about fee collection"
                      rows={3}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="book-call-submit"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Booking…
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" /> Lock my slot
                      </>
                    )}
                  </Button>
                  <p className="text-[10px] text-center text-muted-foreground">
                    We never share your details. Reply to the confirmation email to reschedule anytime.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
